import { makeAutoObservable } from 'mobx';
import featurePermissions from 'main/stores/users/featurePermissions';
import FeaturePermissionViewModel from './FeaturePermissionViewModel';
import {endpoints, types} from 'main/tools';
import User from 'main/stores/users/User';
import Location from 'main/stores/locations/Location';
import Department from 'main/stores/departments/Department';
import _ from 'lodash';

class RoleDetailState {
  store;
  history;
  allFeaturePermissionsDisabled;

  role;
  errors = {};
  selectAllFeaturePermissions = false;
  availableUsers = [];
  featurePermissions = [];
  locations = [];
  departments = [];

  constructor(store, history) {
    makeAutoObservable(this, {
      store: false,
      history: false
    });

    this.store = store;
    this.history = history;
  }

  init() {
    this.availableUsers = this.store.getAllOrdered(types.USER, User);
    this.featurePermissions = featurePermissions.map(f => new FeaturePermissionViewModel(f));

    for (const f of this.featurePermissions) {
      const existing = _.find(this.role.featurePermissions, {name: f.featurePermission.name});
      if (existing) {
        const hardcoded = f.featurePermission;
        f.featurePermission = existing;
        f.featurePermission.merge(hardcoded);
      } else {
        f.featurePermission.enabled = false;
      }
    }

    this.locations = this.store.getAllOrdered(types.LOCATION, Location);
    this.departments = this.store.getAllOrdered(types.DEPARTMENT, Department);
  }

  async load(roleId) {
    this.role = await this.store.loadSingle(roleId);

    await this.store._compose([
      endpoints.LOCATIONS.ALL,
      endpoints.DEPARTMENTS
    ]);

    this.init();

    this.allFeaturePermissionsDisabled = _.every(this.role.featurePermissions, { 'enabled': false });
  }

  async saveRole() {
    const {model, errors} = await this.store.patch(this.role);
    this.errors = errors;
    if (model) {
      this.role.update(model);
      this.history.push('/permissions');
    }
  }

  toggleReports() {
    const checked = this.reportsChecked;
    for (const viewModel of this.featurePermissions) {
      if (viewModel.isReportPermission) {
        viewModel.checked = !checked;
      }
    }
  }

  updateScope(value) {
    this.clearFilters();
    this.role.merge({scope: value});
  }

  clearFilters() {
    this.role.locations = [];
    this.role.departments = [];
  }

  get showPermissions() {
    return !this.role.isAdmin;
  }

  get showAssignees() {
    return this.role.isAdmin || this.role.isCustom;
  }

  get showGeneralInfo() {
    return this.role.isCustom;
  }

  get flatPermissions() {
    return _.reject(this.featurePermissions, viewModel => viewModel.featurePermission.group);
  }

  get payrollPermissions() {
    return _.filter(this.featurePermissions, viewModel => viewModel.featurePermission.group === 'payroll');
  }

  get reportPermissions() {
    return _.chain(this.featurePermissions)
      .filter(viewModel => viewModel.featurePermission.group === 'reports')
      .groupBy(viewModel => viewModel.featurePermission.section)
      .value();
  }

  get reportsChecked() {
    return _.some(this.featurePermissions, viewModel => viewModel.isReportPermission && viewModel.checked);
  }

  get featurePermissionsLocked() {
    return this.role.isManager && this.allFeaturePermissionsDisabled;
  }

  get scopeOptions() {
    let options = ['all_employees', 'direct_reports', 'direct_and_indirect_reports'];
    if (this.role.isCustom) {
      options.splice(
        1, 0, 'all_employees_by_location', 'all_employees_by_department', 'all_employees_by_employment_type'
      );
    }
    return options;
  }
}

export default RoleDetailState;
